/**
 * ToggleRadioBtn.tsx
 *
 * Date: 09-08-2024
 *
 * Description:
 * This component represents a toggle button with a text label. It allows users to toggle between two states.
 *
 * Props:
 * - toggleText (optional): The text to display next to the toggle button.
 * - isToggled (optional): A boolean indicating whether the toggle is in the "on" state.
 * - handleToggle (optional): A function to call when the toggle button is clicked.
 */

import { Switch, CircularProgress, styled, alpha } from "@mui/material";
import { grey, pink } from "@mui/material/colors";
import React from "react";

interface IToggleRadioBtnProps {
  toggleText?: string; // The text to display next to the toggle button
  isToggled?: boolean; // Indicates whether the toggle is in the "on" state
  handleToggle?: () => void; // Function to call when the toggle button is clicked

  loader?: boolean; // loader to disable the radio button while data is loading from the api
  id?: string;
  disabled?: boolean;
}

const ToggleRadioBtn: React.FC<IToggleRadioBtnProps> = ({
  toggleText = "",
  isToggled = false,
  handleToggle = () => {},

  loader = false,
  id,
  disabled = false,
}) => {
  const SwitchStyle = styled(Switch)(({ theme }) => ({
    "& .MuiSwitch-switchBase": {
      color: "#f4f4f4",
      "&:hover": {
        backgroundColor: "transparent",
      },
      "&.Mui-checked": {
        color: "#004540",
        "&:hover": {
          backgroundColor: "transparent",
        },
        "& + .MuiSwitch-track": {
          backgroundColor: "#004540",
          opacity: "1 !important",
        },
      },
      "& + .MuiSwitch-track": {
        backgroundColor: "#ccc",
        opacity: "1 !important",
      },
      transition: theme.transitions.create(["transform"], {
        duration: theme.transitions.duration.standard,
      }),
    },
    "& .MuiSwitch-track": {
      backgroundColor: "#ccc",
      transition: theme.transitions.create(["background-color"], {
        duration: theme.transitions.duration.standard,
      }),
      opacity: "1 !important",
    },
    "& .MuiSwitch-thumb": {
      backgroundColor: isToggled ? "#ffffff" : "#f4f4f4",
      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
      transition: theme.transitions.create(["background-color", "transform"], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
    },
  }));

  return (
    <div
      className="toggle-container"
      id={id}
      style={loader || disabled ? { pointerEvents: "none" } : {}}
    >
      <div className="switch-wrapper">
        <SwitchStyle
          checked={isToggled}
          onChange={handleToggle}
          disabled={loader || disabled}
          disableRipple
        />
        {loader && (
          <div className="loading-overlay">
            <CircularProgress size={16} sx={{ color: "#004540" }} />
          </div>
        )}
      </div>
      {/* Display the text next to the toggle button */}
      <span className="font-size-18px font-Poppins-Regular">{toggleText}</span>
    </div>
  );
};

export default ToggleRadioBtn;
